<template>
	<div>
		<v-card v-if="userisconfirmed" class="mx-auto elevation-0" style="max-width: 500px" :loading="loading">
			<v-card-subtitle v-if="loginError" class="text-center red--text">Error Logging in. Please check your username/password.</v-card-subtitle>

			<v-card-text v-if="!newPasswordRequired">
				<v-form ref="signinform" v-model="valid" lazy-validation>
					<v-text-field v-model="email" label="E-mail" type="text" outlined :rules="emailRules" required />
					<v-text-field
						id="password"
						v-model="password"
						:append-icon="showpassword ? 'mdi-eye' : 'mdi-eye-off'"
						:type="showpassword ? 'text' : 'password'"
						name="password"
						label="Password"
						:counter="password.length > 0"
						outlined
						:rules="passwordRules"
						required
						@click:append="showpassword = !showpassword"
					/>
				</v-form>
			</v-card-text>

			<!-- New Password Required -->
			<v-card-text v-if="newPasswordRequired">
				<v-form ref="signinform" v-model="valid" lazy-validation>
					<v-text-field v-model="email" label="E-mail" type="text" outlined :rules="emailRules" required />
					<v-text-field
						id="password"
						v-model="password"
						label="Password"
						outlined
						:type="showpassword ? 'text' : 'password'"
						:append-icon="showpassword ? 'mdi-eye' : 'mdi-eye-off'"
						required
						name="password"
						:rules="passwordRules"
						@click:append="showpassword = !showpassword"
					></v-text-field>

					<v-card-subtitle class="text-center red--text">You are using a temporary password. Please provide your new password and click sign in.</v-card-subtitle>

					<v-text-field
						id="confirmpassword"
						v-model="confirmpassword"
						label="New Password"
						:rules="[...passwordRules, passwordConfirmationRule]"
						outlined
						:type="showpassword ? 'text' : 'password'"
						:append-icon="showpassword ? 'mdi-eye' : 'mdi-eye-off'"
						name="confirmpassword"
						required
						@click:append="showpassword = !showpassword"
					></v-text-field>
				</v-form>
			</v-card-text>

			<v-card-actions>
				<v-spacer />
				<v-btn class="mb-2 pa-5 text-none" :loading="loading" :disabled="!valid" depressed large rounded color="primary" @click="acceptInvite"
					>Accept
					<v-icon right>mdi-arrow-right</v-icon>
				</v-btn>
				<v-spacer />
			</v-card-actions>
		</v-card>

		<!-- General Error Snackbar -->
		<v-snackbar v-model="errorSnackbar" top color="white" :timeout="6000" class="black--text">
			{{ errorMessage }}
			<v-btn outlined @click="errorSnackbar = false" class="black--text"> Close </v-btn>
		</v-snackbar>

		<!-- Invite Accepted Snackbar -->
		<!-- <v-snackbar v-model="completeDialog" top color="white" :timeout="0" class="black--text">
			Invite Accepted. Would you like to go your dashboard?
			<v-btn outlined @click="redirectToDashboard" class="black--text"> Yes </v-btn>
			<v-btn outlined @click="redirectToAuth" class="black--text"> No </v-btn>
		</v-snackbar> -->

		<v-dialog v-model="completeDialog" persistent max-width="290">
			<v-card>
				<v-card-title class="text-h5"> Invite accepted </v-card-title>
				<v-card-text>Invite Accepted. Would you like to go your dashboard?</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="redirectToAuth"> No </v-btn>
					<v-btn text @click="redirectToDashboard"> Yes </v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="showCompleteInviteDialog" persistent max-width="290">
			<v-card>
				<v-card-title class="text-h5"> Accept invite </v-card-title>
				<v-card-text>Sign-in to complete the invitation process.</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="showCompleteInviteDialog = false"> OK </v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { mapActions } from 'vuex';
import { Tracking } from '@/utils/tracking';

export default {
	data: () => ({
		valid: false,
		userisconfirmed: true,
		showpassword: false,
		showconfirmpassword: false,
		loginError: false,
		password: '',
		confirmpassword: null,
		name: '',
		email: '',
		checkbox: false,
		loading: false,
		errorSnackbar: false,
		completeDialog: false,
		showCompleteInviteDialog: false,
		errorMessage: '',
		confirmcode: '',
		confirmsignupError: false,
		confirmcoderesent: true,
		resendConfirmCodeError: false,
		newPasswordRequired: false,
		rules: {
			email: v => (v || '').match(/@/) || 'Please enter a valid email',
			length: len => v => (v || '').length >= len || `Invalid character length, required ${len}`,
			password: v =>
				(v || '').match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*(_|[^\w])).+$/) || 'Password must contain an upper case letter, a numeric character, and a special character',
			confirmpassword: v =>
				(v || '').match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*(_|[^\w])).+$/) ||
				'Confirm Password must contain an upper case letter, a numeric character, and a special character',
			required: v => !!v || 'This field is required',
		},
		passwordRules: [
			v => !!v || 'Password is required',
			v => (v && v.length >= 5) || 'Password must have 5+ characters',
			v => /(?=.*[A-Z])/.test(v) || 'Must have one uppercase character',
			v => /(?=.*\d)/.test(v) || 'Must have one number',
		],
	}),

	computed: {
		passwordConfirmationRule() {
			return this.password !== this.confirmpassword || 'Password can not be same as temporary password.';
		},

		emailRules() {
			return [v => !!v || 'E-mail is required', v => /.+@.+/.test(v) || 'E-mail must be valid'];
		},
	},

	mounted() {
		Tracking.track('DisplaySignin', {});

		const { challenge, step } = this.$route.query;
		if (challenge && challenge === 'signUp' && step && step === 'completeInvite') {
			this.showCompleteInviteDialog = true;
		}
	},

	methods: {
		...mapActions(['SignIn', 'AcceptCompanyInvite']),

		async signIn() {
			this.$refs.signinform.validate();

			if (!this.valid) return;

			this.loginError = false;
			this.loading = true;

			if (!this.email || !this.password) {
				this.loginError = true;
				this.loading = false;
				return;
			}

			const email = this.email.toLowerCase().trim();
			const password = this.password;

			const result = await this.SignIn({ email, password });

			if (!result.success) {
				if (result.failureType === 'CHALLANGE') {
					if (result.failureValue) {
						this.newPasswordRequired = true;
					}
				} else if (result.failureType === 'ERROR') {
					if (result.failureValue === 'UserNotConfirmedException') {
						this.$router.push('/confirm-signup');
					} else if (result.failureValue === 'PasswordResetRequiredException') {
						this.$router.push({ path: '/reset-password', query: { username: email } });
					}
				}

				this.loginError = true;
				this.loading = false;
				return;
			}

			this.loading = false;

			return result.user;
		},

		async acceptInvite() {
			const { token } = this.$route.query;

			const user = await this.signIn();

			if (!user) {
				this.errorMessage = 'Failed to accept invite';
				this.errorSnackbar = true;
				return;
			}

			const result = await this.completeInviteProcess(user, token);

			if (result) {
				this.completeDialog = true;
			}
		},

		async completeInviteProcess(user, token) {
			if (!user || !user.signInUserSession || !token) {
				this.errorMessage = 'Failed to accept invite';
				this.errorSnackbar = true;
				return false;
			}

			const groups = user.signInUserSession.getAccessToken().payload['cognito:groups'];
			if (!groups.includes('CompanyAdmin')) {
				this.errorMessage = 'You are already part of different group, contact support on what to do next';
				this.errorSnackbar = true;
				return false;
			}

			try {
				await this.AcceptCompanyInvite({ user, token });
				return true;
			} catch (_) {
				this.errorMessage = 'Failed to accept invite';
				this.errorSnackbar = true;
				return false;
			}
		},

		redirectToDashboard() {
			this.$router.push('/dashboard');
		},

		redirectToAuth() {
			this.$router.push('/');
		},
	},
};
</script>

<style></style>
